'use client'

import { useState, useEffect, useCallback } from 'react'
import type { Permission } from '@/lib/permissions.shared'

interface UsePermissionsReturn {
  permissions: Permission[]
  isLoading: boolean
  error: Error | null
  hasPermission: (permission: Permission) => boolean
  hasAnyPermission: (permissions: Permission[]) => boolean
  refresh: () => Promise<void>
}

/**
 * Client-side hook for the current user's effective permissions.
 *
 * Reads them from GET /api/auth/me, which computes them on the server
 * (getUserPermissions: role template + individual grants - denials). It used to
 * query role_permissions / user_permissions straight from the browser with its own
 * Supabase client; whenever that client could not read the session cookie the
 * queries ran as anon, got 401 and the permissions silently became empty
 * (2026-10-04: /home showed only the three keyless tiles, header links vanished).
 *
 * The parameters are kept for compatibility: without a role or a user id there is
 * nobody to ask for, and a change of either triggers a refetch.
 */
export function usePermissions(userRole: string | null, userId?: string | null): UsePermissionsReturn {
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchPermissions = useCallback(async () => {
    if (!userRole && !userId) {
      setPermissions([])
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const res = await fetch('/api/auth/me', { cache: 'no-store' })
      if (!res.ok) throw new Error(`GET /api/auth/me failed with HTTP ${res.status}`)
      const me = await res.json() as { permissions?: unknown }
      const perms = Array.isArray(me.permissions)
        ? me.permissions.filter((p): p is Permission => typeof p === 'string')
        : []

      setPermissions(perms)
    } catch (err) {
      console.error('[usePermissions] could not load permissions:', err)
      setError(err instanceof Error ? err : new Error('Failed to fetch permissions'))
      setPermissions([])
    } finally {
      setIsLoading(false)
    }
  }, [userRole, userId])

  useEffect(() => {
    fetchPermissions()
  }, [fetchPermissions])

  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      return permissions.includes(permission)
    },
    [permissions]
  )

  const hasAnyPermission = useCallback(
    (perms: Permission[]): boolean => {
      return perms.some(p => permissions.includes(p))
    },
    [permissions]
  )

  return {
    permissions,
    isLoading,
    error,
    hasPermission,
    hasAnyPermission,
    refresh: fetchPermissions,
  }
}
